import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";

/**
 * Branding is EMAIL-ONLY.
 *
 * We intentionally do NOT apply branding colors to the app's global CSS.
 * The marketing site + product UI should remain consistent across tenants.
 *
 * Branding values are consumed by:
 * - Admin -> Branding page (edit + preview)
 * - Email preview (iframe srcDoc)
 * - Backend email rendering (source of truth)
 */

const BrandingContext = createContext(null);

export const BrandingProvider = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const [branding, setBranding] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;

    // No user => no branding (public pages must not request tenant data).
    if (!user) {
      setBranding(null);
      setLoading(false);
      return;
    }

    const load = async () => {
      try {
        const res = await fetch("/api/company/branding", {
          credentials: "include",
          cache: "no-store",
        });
        if (!res.ok) throw new Error("Failed to load branding");
        const data = await res.json();
        setBranding(data || {});
      } catch {
        setBranding({});
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [user, authLoading]);

  const refreshBranding = async () => {
    try {
      const res = await fetch("/api/company/branding", {
        credentials: "include",
        cache: "no-store",
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setBranding(data || {});
      return data || {};
    } catch {
      return null;
    }
  };

  const value = useMemo(
    () => ({ branding, loading, refreshBranding, setBranding }),
    [branding, loading],
  );

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
};

export const useBranding = () => useContext(BrandingContext);
